/**
 * Button Group  component props
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BP } from "@adapptio/lib-blueprint";

import {
	dimensionWithConstantPattern,
	ForegroundColorSchema,
	getValuesFromStringEnum,
	ORIENTATION,
	ORIENTATION_default,
	ORIENTATION_opts,
	propGroups,
	SPACING,
	termsEditor as LibComponentsTerms
} from "@adapptio/lib-components";

import { termsEditor } from "../../terms";
import { ComponentButton_Props } from "../Button/props";

/**
 * Button Group Item Inherited props
 */
const ButtonGroupItemInheritedProps = {
	itemOverrideStyle: BP.Prop(
		BP.Boolean({
			...termsEditor.schemas.buttonGroup.contentItemUseParentStyle,
			default: true,
			fallbackValue: true,
			constraints: {
				required: true
			}
		}),
		0
	)
};

/**
 * Button Group Item Inherited props type
 */
export type TButtonGroupItemInheritedProps = typeof ButtonGroupItemInheritedProps;

/**
 * Button Group props
 */
export const ComponentButtonGroup_Props = {
	content: BP.Prop(
		BP.ComponentList({
			...termsEditor.schemas.buttonGroup.content,
			alias: "buttonGroupContent",
			allowComponentNames: [ "button", "menuButton", "repeat", "oneOf", "routes" ],
			inheritedProps: {
				...ButtonGroupItemInheritedProps
			}
		}),
		0,
		propGroups.common
	),

	typeData: BP.Prop(
		BP.OneOf({
			...termsEditor.schemas.buttonGroup.typeData,
			typeValueOpts: {
				...termsEditor.schemas.buttonGroup.typeData
			},
			defaultType: "NONE",
			types: {
				NONE: {
					...termsEditor.schemas.buttonGroup.typeDataValues.none,
					value: BP.Void({})
				},
				TOGGLE: {
					...termsEditor.schemas.buttonGroup.typeDataValues.toggle,
					value: BP.Void({})
				},
				SWITCH: {
					...termsEditor.schemas.buttonGroup.typeDataValues.switch,
					value: BP.Object({
						props: {
							activeRequired: BP.Prop(
								BP.Boolean({
									...termsEditor.schemas.buttonGroup.activeRequired,
									default: false,
									fallbackValue: false,
									constraints: {
										required: true
									}
								}),
								10
							)
						},
						editorOptions: {
							layoutType: "passthrough"
						}
					})
				}
			}
		}),
		10,
		propGroups.common
	),

	orientation: BP.Prop(
		BP.Enum.String({
			...LibComponentsTerms.schemas.common.orientation,
			options: getValuesFromStringEnum(
				ORIENTATION,
				LibComponentsTerms.schemas.common.orientationValues,
				ORIENTATION_opts
			),
			default: ORIENTATION_default,
			fallbackValue: ORIENTATION_default,
			constraints: {
				required: true
			},
			editorOptions: {
				controlType: "buttons"
			}
		}),
		20,
		propGroups.common
	),

	fieldName: BP.Prop(
		BP.String({
			...termsEditor.schemas.field.name,
			default: null,
			fallbackValue: null,
			constraints: {
				required: false
			}
		}),
		90,
		propGroups.state
	),

	styleData: BP.Prop(ComponentButton_Props.styleData.schema, 0, propGroups.style),

	borderRadiusGroup: BP.Prop(
		BP.Boolean({
			...termsEditor.schemas.buttonGroup.borderRadiusGroup,
			default: true,
			fallbackValue: true,
			constraints: {
				required: true
			}
		}),
		10,
		propGroups.style
	),

	separatorColor: BP.Prop(
		ForegroundColorSchema({
			...termsEditor.schemas.buttonGroup.separatorColor,
			alias: "foregroundColor_buttonGroupSeparator",
			default: "TRANSPARENT"
		}),
		20,
		propGroups.style
	),

	separatorSize: BP.Prop(
		BP.StringWithConst({
			...termsEditor.schemas.buttonGroup.separatorSize,
			constants: getValuesFromStringEnum(SPACING, LibComponentsTerms.schemas.common.spacingValues),
			default: "",
			constraints: {
				...LibComponentsTerms.schemas.common.spacingConstraints,
				pattern: dimensionWithConstantPattern
			}
		}),
		30,
		propGroups.style
	)
};
